/**
 * NavLogViewer — displays the navigation log as an accessible table with print/export.
 */
import type { NavigationLog } from '@efb/shared-core';
import { FormatPrettyPrinter } from '@efb/shared-core';

interface Props {
  navLog: NavigationLog | null;
  onClose: () => void;
}

export function NavLogViewer({ navLog, onClose }: Props) {
  if (!navLog) {
    return (
      <div className="panel navlog-panel" role="region" aria-label="Navigation Log">
        <h2>Navigation Log</h2>
        <p>No navigation log generated yet. Create a flight plan and generate a nav log first.</p>
        <button className="fp-btn" onClick={onClose}>Close</button>
      </div>
    );
  }

  const handlePrint = () => {
    const printer = new FormatPrettyPrinter();
    const text = printer.printNavLog(navLog);
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(`<pre style="font-family:monospace;font-size:12px">${text}</pre>`);
      w.document.close();
      w.print();
    }
  };

  const handleCopyText = () => {
    const printer = new FormatPrettyPrinter();
    navigator.clipboard.writeText(printer.printNavLog(navLog));
  };

  return (
    <div className="panel navlog-panel" role="region" aria-label="Navigation Log">
      <div className="navlog-header">
        <h2>Navigation Log</h2>
        <div className="navlog-actions">
          <button className="fp-btn" onClick={handlePrint} title="Print">🖨️ Print</button>
          <button className="fp-btn" onClick={handleCopyText} title="Copy text">📋 Copy</button>
          <button className="fp-btn" onClick={onClose}>✕</button>
        </div>
      </div>

      <div className="navlog-totals">
        <span>Distance: {navLog.totalDistanceNm.toFixed(1)} nm</span>
        <span>Time: {Math.floor(navLog.totalTimeMinutes / 60)}h {Math.round(navLog.totalTimeMinutes % 60)}m</span>
        <span>Fuel: {navLog.totalFuelGallons.toFixed(1)} gal</span>
      </div>

      <div className="navlog-table-wrapper" tabIndex={0} role="table" aria-label="Navigation log legs">
        <table className="navlog-table">
          <thead>
            <tr>
              <th>From</th><th>To</th><th>TC</th><th>MC</th><th>HDG</th>
              <th>GS</th><th>Dist</th><th>ETE</th><th>Fuel</th><th>Cum Fuel</th>
            </tr>
          </thead>
          <tbody>
            {navLog.legs.map((leg, i) => (
              <tr key={i}>
                <td>{leg.fromWaypoint}</td>
                <td>{leg.toWaypoint}</td>
                <td>{Math.round(leg.trueCourse)}°</td>
                <td>{Math.round(leg.magneticCourse)}°</td>
                <td>{Math.round(leg.windCorrectedHeading)}°</td>
                <td>{Math.round(leg.groundSpeed)}</td>
                <td>{leg.distanceNm.toFixed(1)}</td>
                <td>{Math.round(leg.estimatedTimeMinutes)}m</td>
                <td>{leg.fuelBurnGallons.toFixed(1)}</td>
                <td>{leg.cumulativeFuelGallons.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
